import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const navItems = [
  {
    label: "Soluciones",
    children: [
      { label: "Revenue Management", href: "/revenue-management-hoteles", description: "Optimiza tus ingresos con estrategias avanzadas" },
      { label: "Software RMS", href: "/software-rms", description: "Plataforma integral de gestión de ingresos" },
      { label: "Pricing Dinámico", href: "/pricing-dinamico", description: "Tarifas optimizadas en tiempo real" },
      { label: "Forecasting", href: "/forecasting-hotelero", description: "Predicción de demanda con IA" },
    ]
  },
  { label: "RevPAR", href: "/revpar" },
  { label: "Blog", href: "/blog" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToContact = () => {
    if (location.pathname === '/') {
      const element = document.getElementById('demo-form');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/#demo-form');
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-effect" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto container-padding">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://zimoorevenue.com/wp-content/uploads/2024/08/logotipo.png"
              alt="Zimoo Revenue"
              className="h-8 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavigationMenu>
              <NavigationMenuList>
                {navItems.map((item) => (
                  <NavigationMenuItem key={item.label}>
                    {item.children ? (
                      <>
                        <NavigationMenuTrigger className="bg-transparent text-[#0A0A0A] hover:text-[#1ABCB4] font-medium">
                          {item.label}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className="grid w-[400px] gap-3 p-4">
                            {item.children.map((child) => (
                              <li key={child.href}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    to={child.href}
                                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-[#FAFAFA] focus:bg-[#FAFAFA]"
                                    data-testid={`nav-${child.href.replace(/\//g, '-')}`}
                                  >
                                    <div className="text-sm font-medium text-[#0A0A0A]">
                                      {child.label}
                                    </div>
                                    <p className="line-clamp-2 text-sm text-[#404040]">
                                      {child.description}
                                    </p>
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </NavigationMenuContent>
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className="px-4 py-2 text-sm font-medium text-[#0A0A0A] hover:text-[#1ABCB4] transition-colors"
                        data-testid={`nav-${item.href.replace(/\//g, '-')}`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:+529981475384" className="text-sm text-[#404040] hover:text-[#1ABCB4] transition-colors">
              998 147 5384
            </a>
            <Button data-testid="header-cta-button" className="btn-primary" onClick={scrollToContact}>
              Contáctanos
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-[#0A0A0A]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-black/5 shadow-lg" data-testid="mobile-menu">
            <nav className="container-padding py-4">
              {navItems.map((item) => (
                <div key={item.label} className="py-2">
                  {item.children ? (
                    <details className="group">
                      <summary className="flex items-center justify-between cursor-pointer py-2 text-[#0A0A0A] font-medium">
                        {item.label}
                        <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="pl-4 mt-2 space-y-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="block py-2 text-[#404040] hover:text-[#1ABCB4]"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <Link
                      to={item.href}
                      className="block py-2 text-[#0A0A0A] font-medium hover:text-[#1ABCB4]"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 mt-4 border-t border-black/5">
                <a href="tel:+529981475384" className="block py-2 text-[#404040]">
                  998 147 5384
                </a>
                <Button className="btn-primary w-full mt-2" onClick={scrollToContact}>Contáctanos</Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
